import React, { ReactNode } from "react";
import { Autocomplete, Box, Button, MenuItem, Select, TextField } from "@mui/material";
import { BulkTextInputDialog } from "components/dialogs/BulkTextInputDialog";
import { SimpleFormLabel } from "components/dialogs/SimpleFormDialog";
import { DescriptiveSelect } from "components/uiElements/inputs/DescriptiveSelect";
import { QueryFieldsSelector } from "components/queries/queryBuilder/dialogParts/QueryFieldsSelector";
import { useTranslation } from "react-i18next";
import { QueryInfo } from "system/query/QueryInfo";
import { CLUSTERING_BIAS_OPTIONS, ClusteringBias } from "components/widgets/utility/ClusteringBias";

interface TopicQuerySettingsProps {
  apiData: string[];
  bulkExclusionDialogIsOpen: boolean;
  children?: ReactNode;
  clusteringBias?: ClusteringBias;
  excludedTerms: string[] | [];
  queryInfo: QueryInfo;
  queryTermExclusion: string;
  selectedFieldIds: string[] | [];
  selectedTopicsTypes: string[];
  sortBy?: string;
  setBulkExclusionDialogIsOpen(value: boolean): void;
  setClusteringBias?(value: ClusteringBias): void;
  setExcludedTerms(values: string[] | []): void;
  setQueryTermExclusion(value: string): void;
  setSelectedFieldIds(value: string[]): void;
  setSelectedTopicsTypes(value: string[]): void;
  setSortBy?(value: string): void;
  topicsTypes?: Record<string, string>[];
}

export default function TopicQuerySettings(props: TopicQuerySettingsProps): React.JSX.Element {
  const { i18n } = useTranslation();

  const topicsTypes = [
    { id: "topic", label: i18n.t("requestOptions.topics-types.topic") },
    { id: "hashtag", label: i18n.t("requestOptions.topics-types.hashtag") },
    { id: "mention", label: i18n.t("requestOptions.topics-types.mention") },
    { id: "link", label: i18n.t("requestOptions.topics-types.link") },
    { id: "domain", label: i18n.t("requestOptions.topics-types.domain") },
    { id: "emoji", label: i18n.t("requestOptions.topics-types.emoji") },
    { id: "flag", label: i18n.t("requestOptions.topics-types.flag" )}
  ];

  const sortOptions = [
    { value: "relevance", label: i18n.t("relevance") },
    { value: "count", label: i18n.t("record-count") },
    { value: "appearances", label: i18n.t("appearances") },
    { value: "rising", label: i18n.t("rising") },
    { value: "falling", label: i18n.t("falling") }
  ];

  return (
    <>
      <SimpleFormLabel>{i18n.t("fields-to-analyze")}</SimpleFormLabel>
      <QueryFieldsSelector
        allowedFieldTypes={["text"]}
        selectedFieldIds={props.selectedFieldIds}
        setSelectedFieldIds={(newFieldIds: string[]) => {
          props.setSelectedFieldIds(newFieldIds);
        }}
        queryInfo={props.queryInfo}
        requireAggSupported={true}
        placeholder={i18n.t("using-dataset-defaults")}
      />
      <SimpleFormLabel>{i18n.t("category")}</SimpleFormLabel>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={props.topicsTypes ?? topicsTypes}
        defaultValue={[]}
        value={(props.topicsTypes ?? topicsTypes).filter((topicsType: any) => props.selectedTopicsTypes.includes(topicsType.id)) || []}
        onChange={(event: React.SyntheticEvent, newValue: any[] | null) => {
          if (newValue) { props.setSelectedTopicsTypes(newValue.map(field => field.id)) }
        }}
        renderInput={(params) => <TextField autoComplete="off" {...params} />} />
      <SimpleFormLabel>{i18n.t("widget-types.word-cloud.settings.excluded-terms")}</SimpleFormLabel>
      <Autocomplete
        multiple
        freeSolo
        disableCloseOnSelect
        options={props.apiData.sort((a:string, b:string) => a.localeCompare(b))}
        defaultValue={[]}
        value={props.excludedTerms}
        onChange={(event: React.SyntheticEvent, newValues: string[]) => {
          if (newValues) { props.setExcludedTerms(newValues) }
        }}
        renderInput={(params) => <TextField autoComplete="off" {...params} />} />
      <Button
        onClick={() => props.setBulkExclusionDialogIsOpen(true)}
        size="small"
        variant="outlined"
        sx={{ mt: 1 }} 
      >
        {i18n.t("query-form.bulk-add-values")}
      </Button>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 2 }}>
        {props.setSortBy && (
          <Box>
            <SimpleFormLabel>{i18n.t("sort by")}</SimpleFormLabel>
            <Select
              fullWidth
              value={props.sortBy ?? "relevance"}
              onChange={(event) => props.setSortBy?.(event.target.value)}
            >
              {sortOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </Box>
        )}
        {props.setClusteringBias && (
          <Box>
            <SimpleFormLabel>{i18n.t("widget-settings.clustering-bias.title")}</SimpleFormLabel>
            <Select
              fullWidth
              value={props.clusteringBias ?? ClusteringBias.Mid}
              onChange={(event) => props.setClusteringBias?.(event.target.value as ClusteringBias)}
            >
              {CLUSTERING_BIAS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{i18n.t(option.labelKey)}</MenuItem>
              ))}
            </Select>
          </Box>
        )}
        <Box>
          <SimpleFormLabel>{i18n.t("requestOptions.query-term-exclusion")}</SimpleFormLabel>
          <DescriptiveSelect
            fullWidth={true}
            value={props.queryTermExclusion}
            onChange={(value) => props.setQueryTermExclusion(value as string)}
            options={["all", "required", "none", "only"].map((option) => ({
              value: option,
              label: i18n.t(option),
              description: i18n.t(`requestOptions.query-term-exclusion-tooltip-${option}`)
            }))}
          />
        </Box>
      </Box>
      {props.children}
      {props.bulkExclusionDialogIsOpen &&
        <BulkTextInputDialog
          onClose={() => props.setBulkExclusionDialogIsOpen(false)}
          onSubmit={(bulkValues: string[]) => {
            if (bulkValues.length) {
              props.setExcludedTerms([...props.excludedTerms, ...bulkValues]);
            }
            props.setBulkExclusionDialogIsOpen(false);
          }}
        />
      }
    </>
  );
}